/**
 * Footer Menu Behavior Script
 * Controls the accordion behavior of footer menus with details/summary tags
 * Based on Shopify Dawn theme implementation
 */

class FooterMenu {
  constructor() {
    this.init();
  }

  init() {
    this.setupAccordionBehavior();
    this.setupMobileBehavior();
    this.setupKeyboardNavigation();
    this.forceDesktopOpen();
  }

  /**
   * Setup accordion behavior - only one menu open at a time on mobile
   */
  setupAccordionBehavior() {
    const detailsElements = document.querySelectorAll('.footer-menu-details');
    
    detailsElements.forEach((detail) => {
      detail.addEventListener('toggle', (event) => {
        // Only apply accordion behavior on mobile
        if (window.innerWidth < 750 && event.target.open) {
          // Close other open menus in the same group
          const parentContainer = event.target.closest('.footer-block__menu-content, .footer-block__header-menu');
          if (parentContainer) {
            const otherDetails = parentContainer.querySelectorAll('.footer-menu-details[open]');
            otherDetails.forEach((otherDetail) => {
              if (otherDetail !== event.target) {
                otherDetail.removeAttribute('open');
              }
            });
          }
        } else if (window.innerWidth >= 750) {
          // On desktop, prevent closing and force open
          event.target.setAttribute('open', '');
        }
      });
    });
  }

  /**
   * Setup mobile-specific behavior
   */
  setupMobileBehavior() {
    // Handle window resize to adjust behavior
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });

    // Initial setup
    this.handleResize();
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    const detailsElements = document.querySelectorAll('.footer-menu-details');
    
    if (window.innerWidth >= 750) {
      // Desktop: Force open all menus and disable interaction
      detailsElements.forEach((detail) => {
        detail.setAttribute('open', '');
        // Force display of content
        const content = detail.querySelector('.footer-menu-content');
        if (content) {
          content.style.display = 'block';
        }
        // Disable summary interaction on desktop
        const summary = detail.querySelector('.footer-menu-summary');
        if (summary) {
          summary.style.pointerEvents = 'none';
        }
      });
    } else {
      // Mobile: Close all menus initially and enable interaction
      detailsElements.forEach((detail) => {
        detail.removeAttribute('open');
        // Let CSS handle content display on mobile
        const content = detail.querySelector('.footer-menu-content');
        if (content) {
          content.style.display = '';
        }
        // Enable summary interaction on mobile
        const summary = detail.querySelector('.footer-menu-summary');
        if (summary) {
          summary.style.pointerEvents = 'auto';
        }
      });
    }
  }

  /**
   * Force all menus to be open on desktop
   */
  forceDesktopOpen() {
    if (window.innerWidth >= 750) {
      const detailsElements = document.querySelectorAll('.footer-menu-details');
      detailsElements.forEach((detail) => {
        detail.setAttribute('open', '');
        detail.style.display = 'block';
        
        const content = detail.querySelector('.footer-menu-content');
        if (content) {
          content.style.display = 'block';
        }
        
        const summary = detail.querySelector('.footer-menu-summary');
        if (summary) {
          summary.style.pointerEvents = 'none';
        }
      });
    } else {
      // On mobile, ensure proper initial state
      const detailsElements = document.querySelectorAll('.footer-menu-details');
      detailsElements.forEach((detail) => {
        detail.removeAttribute('open');
        detail.style.display = '';
        
        const content = detail.querySelector('.footer-menu-content');
        if (content) {
          content.style.display = '';
        }
        
        const summary = detail.querySelector('.footer-menu-summary');
        if (summary) {
          summary.style.pointerEvents = 'auto';
        }
      });
    }
  }

  /**
   * Setup keyboard navigation for accessibility
   */
  setupKeyboardNavigation() {
    const summaryElements = document.querySelectorAll('.footer-menu-summary');
    
    summaryElements.forEach((summary) => {
      summary.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const details = summary.closest('.footer-menu-details');
          if (details && window.innerWidth < 750) {
            // Only allow keyboard interaction on mobile
            details.toggleAttribute('open');
          }
        }
      });
    });
  }

  /**
   * Public method to refresh the menu behavior
   * Useful when new menus are dynamically added
   */
  refresh() {
    this.init();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new FooterMenu();
});

// Re-initialize if new content is loaded (for dynamic themes)
document.addEventListener('shopify:section:load', () => {
  new FooterMenu();
});

// Export for potential external use
window.FooterMenu = FooterMenu;
